import { useState } from 'react';
import { Button } from '@/shared/components/common/Button/Button';
import type { UserInterface } from '@/shared/model/api.types';
import type { SalesPeriod, SalesReport, TransferInput } from './types';
import { formatDate, isEligibleRecipient, money, remainingPreview, todayInMoscow } from './utils';
import { useReport } from './useReport';
import css from './PlanHistory.module.css';

type Props = {
  report: SalesReport;
  users: UserInterface[];
  usersLoading: boolean;
  usersError: string | null;
  busy: boolean;
  onTransfer: (input: TransferInput) => Promise<void>;
};

export function TransferForm({ report, users, usersLoading, usersError, busy, onTransfer }: Props) {
  const today = todayInMoscow();
  const [monthNumber, setMonthNumber] = useState(report.months.find(m => m.dateStart <= today && m.dateEnd >= today)?.month ?? report.months[0].month);
  const selected = report.months.find(m => m.month === monthNumber)!;
  const [revision, setRevision] = useState(0);
  const period: SalesPeriod = { departmentId: report.departmentId, period: 'month', year: selected.year, month: selected.month };
  // A quarter may only be partially frozen. Always check the exact month before enabling transfer.
  const monthRequest = useReport(report.period === 'quarter' ? period : null, revision);
  const monthReport = report.period === 'month' ? report : monthRequest.data;
  const month = monthReport?.months[0];
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [date, setDate] = useState(today >= selected.dateStart && today <= selected.dateEnd ? today : selected.dateStart);
  const [confirm, setConfirm] = useState(false);
  const sources = month?.employees.filter(e => !e.responsibleUntil && (e.opportunity.target > 0 || e.profit.target > 0)) ?? [];
  const source = sources.find(e => e.userId === Number(fromId));
  const recipients = month ? users.filter(u => isEligibleRecipient(u, report.departmentId, Number(fromId), month)) : [];
  const recipient = recipients.find(u => u.id === Number(toId));
  const minDate = [selected.dateStart, source?.responsibleFrom ?? '', ...(month?.transfers.map(t => t.effectiveDate) ?? [])].sort().slice(-1)[0];
  const maxDate = selected.dateEnd < today ? selected.dateEnd : today;
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date) && date >= minDate && date <= maxDate;
  const valid = !!month && !monthReport?.snapshot && !!source && !!recipient && validDate && !usersLoading && !usersError;
  const remaining = source && validDate ? remainingPreview(source, date) : null;

  return <section className={css.section}>
    <h3>Передача остатка плана</h3>
    <p>Передаются основные планы по обороту и прибыли только за выбранный месяц. Собственный план получателя сохраняется.
      Планы следующих месяцев нужно назначить отдельно.</p>
    {report.period === 'quarter' && <label className={css.field}>Месяц передачи
      <select value={monthNumber} disabled={busy} onChange={e => {
        const next = report.months.find(m => m.month === Number(e.target.value))!;
        setMonthNumber(next.month); setFromId(''); setToId(''); setDate(next.dateStart); setConfirm(false);
      }}>{report.months.map(m => <option key={m.month} value={m.month}>{formatDate(m.dateStart)} — {formatDate(m.dateEnd)}</option>)}</select>
    </label>}
    {monthRequest.loading && <p role="status">Проверяем статус месяца…</p>}
    {monthRequest.error && <p role="alert">{monthRequest.error} <Button size="s" onClick={() => setRevision(n => n + 1)}>Повторить проверку месяца</Button></p>}
    {monthReport?.snapshot ? <p className={css.notice}>Этот месяц уже зафиксирован. Передача недоступна.</p> : month && <>
      <div className={css.fields}>
        <label className={css.field}>Передающий сотрудник
          <select value={fromId} disabled={busy} onChange={e => { setFromId(e.target.value); setToId(''); setConfirm(false); }}>
            <option value="">Выберите сотрудника</option>
            {sources.map(e => <option key={e.userId} value={e.userId}>{e.name} (#{e.userId})</option>)}
          </select>
        </label>
        <label className={css.field}>Новый ответственный
          <select value={toId} disabled={busy || usersLoading || !!usersError || !source} onChange={e => { setToId(e.target.value); setConfirm(false); }}>
            <option value="">Выберите сотрудника</option>
            {recipients.map(u => <option key={u.id} value={u.id}>{[u.last_name, u.name, u.second_name].filter(Boolean).join(' ')} (#{u.id})</option>)}
          </select>
        </label>
        <label className={css.field}>Передать с даты (включительно)
          <input type="date" value={date} min={minDate} max={maxDate} disabled={busy} onChange={e => { setDate(e.target.value); setConfirm(false); }}/>
        </label>
      </div>
      {!sources.length && <p>Нет сотрудников с доступным для передачи основным планом.</p>}
      {usersLoading && <p role="status">Загружаем сотрудников…</p>}
      {usersError && <p role="alert">Не удалось загрузить получателей: {usersError}</p>}
      {source && !usersLoading && !usersError && !recipients.length && <p>Нет доступных действующих сотрудников этого отдела для приёма плана.</p>}
      {!validDate && <p>Дата должна быть в выбранном месяце, не раньше предыдущих передач и не позже сегодняшнего дня (МСК).</p>}
      {remaining && <p className={css.notice}>
        Предварительный остаток: оборот {money(remaining.opportunity)}, прибыль {money(remaining.profit)}.
        Учитываются только закрытые сделки до {formatDate(date)}, без самого дня передачи.
        Счета и «к отгрузке» остаток не уменьшают. Точную сумму рассчитает сервер при подтверждении.
      </p>}
      <Button disabled={busy || !valid} onClick={() => setConfirm(true)}>Передать остаток</Button>
      {confirm && valid && <div className={css.confirm}>
        <p>Передать план от {source!.name} сотруднику {recipient!.last_name} {recipient!.name} с {formatDate(date)}?
          Общий план отдела сохранится. История выполнения первого сотрудника будет зафиксирована.
          После передачи обычное редактирование основных планов отдела за этот месяц блокируется. Отмены через интерфейс нет.</p>
        <div className={css.actions}>
          <Button variant="warning" disabled={busy} onClick={() => {
            setConfirm(false);
            void onTransfer({ departmentId: report.departmentId, fromUserId: source!.userId, toUserId: recipient!.id, effectiveDate: date });
          }}>Подтвердить передачу</Button>
          <Button variant="secondary" disabled={busy} onClick={() => setConfirm(false)}>Отмена передачи</Button>
        </div>
      </div>}
    </>}
  </section>;
}
